<template>
  <div v-loading="isSaving">
    <b-list-group style="max-height: 375px" class="overflow-auto" flush>
      <template v-if="isFetching">
        <BDXStatusModalItemSkeleton v-for="key in globalUMRs" :key="key" />
      </template>
      <template v-else>
        <div v-if="isEmpty(globalUMRs)">No items found.</div>
        <b-list-group
          v-else
          style="max-height: 375px"
          class="overflow-auto"
          flush
        >
          <b-list-group-item
            v-for="({ umr, subItems }, index) in globalUMRs"
            :key="umr"
            class="py-1 px-0"
          >
            <div class="d-flex align-items-center">
              <base-button
                v-if="!isEmpty(subItems)"
                v-b-toggle="`collapse-${index}`"
                @click="() => onToggleCollapse(umr)"
                type="white"
                class="p-1 mr-2 border border-primary shadow-none"
                round
                style="font-size: 11.52px"
              >
                <b-icon-dash v-if="collapsedItems.includes(umr)" />
                <b-icon-plus v-else />
              </base-button>
              <BDXStatusModalFormFCPToggle
                v-if="showFCPToggle"
                v-model="globalUMRs[index].fcpStatus"
                :start-date="globalUMRs[index].startDate"
                @update:start-date="
                  (newStartDate) => onUpdateStartDate(newStartDate, umr)
                "
                @input="(value) => onToggleParentFCPStatus(value, umr)"
              >
                <base-switch
                  @input="(value) => onToggleParentStatus(value, umr)"
                  v-model="globalUMRs[index].bdxStatus"
                  on-text="UMR"
                  off-text="UMR"
                  :class="{ 'mx-2': showFCPToggle, 'mr-2': !showFCPToggle }"
                />
                <span class="lh-120">{{ umr }}</span>
              </BDXStatusModalFormFCPToggle>
              <span v-else class="lh-120">{{ umr }}</span>
            </div>

            <b-collapse
              v-if="!isEmpty(subItems)"
              :id="`collapse-${index}`"
              class="mt-2"
            >
              <b-list-group
                flush
                :style="{ 'padding-left': showFCPToggle ? '84px' : '24px' }"
              >
                <BDXStatusModalItem
                  v-for="(
                    { index, parent, umr, cat_code, risk_code, section_no }, key
                  ) in subItems"
                  v-model="umrStatusList[index].bdx_status"
                  @input="() => onToggleStatus(parent)"
                  :key="key"
                >
                  <span class="lh-120"
                    >{{ umr }} {{ risk_code || 0 }} {{ cat_code || 0 }}
                    {{ section_no || 0 }}</span
                  >
                </BDXStatusModalItem>
              </b-list-group>
            </b-collapse>
          </b-list-group-item>
        </b-list-group>
      </template>
    </b-list-group>
    <div class="font-weight-bold text-center mt-2" style="font-size: 14px">
      Legend: UMR . Risk Code . Cat Code . Section No
    </div>
    <div class="py-4 d-flex align-items-center justify-content-between">
      <BDXStatusModalPagination :total-rows="totalPages" />

      <b-button @click="handleSave" :disabled="isFetching" variant="primary"
        >Save</b-button
      >
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import {
  defineComponent,
  computed,
  inject,
  ref,
  watch
} from '@vue/composition-api'
import { flatten, keys, range, isEmpty } from 'lodash-es'
import { format, parse } from 'date-fns'

import { DATE_FORMAT } from '@constants'
import { useTryCatch, useCurrentUser, useUserCompany } from '@composables'
import { GlobalUMRs } from '@services'

import BDXStatusModalItem from './BDXStatusModalItem.vue'
import BDXStatusModalItemSkeleton from './BDXStatusModalItemSkeleton.vue'
import BDXStatusModalPagination from './BDXStatusModalPagination.vue'
import BDXStatusModalFormFCPToggle from './BDXStatusModalFormFCPToggle.vue'

export default defineComponent({
  name: 'BDXStatusForm',
  components: {
    BDXStatusModalItem,
    BDXStatusModalItemSkeleton,
    BDXStatusModalPagination,
    BDXStatusModalFormFCPToggle
  },
  emits: ['saved'],
  setup(props, { emit }) {
    const { userEmail, fcpRole } = useCurrentUser()
    const { roleName: companyRoleName, roleValue: companyRoleValue } =
      useUserCompany()

    const { filters, pagination, setPagination } = inject('global-umr-modal')

    const isFetching = ref(true)
    const isSaving = ref(false)

    const umrStatusList = ref([])

    const searchFilter = ref()

    const totalPages = ref(0)

    // INFO: Global umrs logical concerns
    const globalUMRs = ref([])

    const getGlobalUMRs = async () => {
      isFetching.value = true

      globalUMRs.value = keys(range(1, 10))

      try {
        let {
          data: { count, results }
        } = await GlobalUMRs.getItems({
          ...filters.value,
          ...pagination.value,
          role: companyRoleName.value,
          role_value: companyRoleValue.value,
          sort: 'umr'
        })

        let i = -1

        const transformedResults = Object.entries(results)
          .sort(([key1], [key2]) => key1.localeCompare(key2))
          .map((result) => {
            const [key, items] = result
            const subItems = items.map((item) => {
              i++

              return {
                ...item,
                index: i,
                parent: key
              }
            })

            const bdxStatus = !subItems
              .map(({ bdx_status }) => bdx_status)
              .includes(true)
              ? false
              : true

            const fcpStatus = subItems
              .map(({ fcp_status }) => fcp_status)
              .includes(false)
              ? false
              : true

            const { fcp_start_date: startDate } = subItems[0] || {}

            return {
              bdxStatus,
              fcpStatus,
              startDate,
              subItems,
              umr: key
            }
          })

        umrStatusList.value = flatten(
          transformedResults.map(({ subItems }) =>
            subItems.map((subItem) => ({
              id: subItem.id,
              bdx_status: subItem.bdx_status,
              fcp_status: subItem.fcp_status,
              fcp_start_date: subItem.fcp_start_date,
              currency_type: subItem.currency_type
            }))
          )
        )

        globalUMRs.value = transformedResults
        totalPages.value = count

        isFetching.value = false
      } catch (err) {
        const { message } = err

        isFetching.value = false

        umrStatusList.value = []

        Vue.prototype.$toasted.error(message)
      }
    }

    watch([filters], () =>
      setPagination({
        ...pagination.value,
        page: 1
      })
    )

    watch([filters, pagination], getGlobalUMRs, {
      deep: true,
      immediate: true
    })

    const handleSave = async () => {
      isSaving.value = true

      await useTryCatch(async () => {
        await GlobalUMRs.updateItems(userEmail.value, umrStatusList.value)

        Vue.prototype.$toasted.show('UMRs updated successfully')

        emit('saved')
      })

      isSaving.value = false
    }

    // INFO: Collapse logical concerns
    const collapsedItems = ref([])

    const onToggleCollapse = (umr) => {
      const index = collapsedItems.value.indexOf(umr)

      if (0 <= index) collapsedItems.value.splice(index, 1)
      else collapsedItems.value.push(umr)
    }

    // INFO: FCP status logical concerns
    const showFCPToggle = computed(() => (fcpRole.value ? true : false))

    const onToggleParentFCPStatus = (fcpStatus, umr) => {
      const { subItems } = globalUMRs.value.find(
        (globalUMR) => umr == globalUMR.umr
      )

      if (isEmpty(subItems)) return

      subItems.forEach(
        ({ index }) => (umrStatusList.value[index].fcp_status = fcpStatus)
      )
    }

    // INFO: FCP start date logical concerns
    const onUpdateStartDate = (newStartDate, umr) => {
      const index = globalUMRs.value.findIndex(
        (globalUMR) => umr === globalUMR.umr
      )
      if (0 > index) return

      globalUMRs.value[index].startDate = newStartDate
      globalUMRs.value[index].subItems.forEach(
        ({ index }) =>
          (umrStatusList.value[index].fcp_start_date = format(
            parse(newStartDate),
            DATE_FORMAT
          ))
      )
    }

    // INFO: Status logical concerns
    const onToggleParentStatus = (bdxStatus, umr) => {
      const { subItems } = globalUMRs.value.find(
        (globalUMR) => umr == globalUMR.umr
      )

      if (isEmpty(subItems)) return

      subItems.forEach(
        ({ index }) => (umrStatusList.value[index].bdx_status = bdxStatus)
      )
    }

    const onToggleStatus = (parentName) => {
      const index = globalUMRs.value.findIndex(
        (globalUMR) => parentName == globalUMR.umr
      )

      if (0 > index) return

      const { bdxStatus, subItems } = globalUMRs.value[index]

      if (isEmpty(subItems)) return

      const subItemsStatus = subItems.map(({ index }) => {
        const { bdx_status } = umrStatusList.value[index]

        return bdx_status
      })

      /*

      if (bdxStatus && subItemsStatus.every((value) => !value))
        globalUMRs.value[index].bdxStatus = false
      else if (!bdxStatus && subItemsStatus.every((value) => value))
        globalUMRs.value[index].bdxStatus = true
      */
    }

    return {
      isEmpty,

      isFetching,
      isSaving,

      globalUMRs,

      umrStatusList,

      searchFilter,

      totalPages,

      collapsedItems,
      onToggleCollapse,

      showFCPToggle,
      onToggleParentFCPStatus,

      onUpdateStartDate,

      onToggleParentStatus,
      onToggleStatus,

      handleSave
    }
  }
})
</script>
